/**
 * Shared multi-signatory eligibility helper (issue #799, @dylan219).
 *
 * When an account requires more than one signature to transact (a joint
 * account with a "two to sign" mandate), a set of account-changing servicing
 * actions must NOT be permitted through the agent desktop — the change has to go
 * through the mandate. Per @dylan219's ruling the blocked actions are:
 *
 *   Payment section  — amending a payee; setting up payees (third-party
 *                      payment); Direct Debit cancellation; Standing Order
 *                      cancellation.
 *   Chequebook section — Chequebook Order; Stopping cheque(s).
 *
 * The signal is the per-account signatory count the Bank gateway returns on
 * `customer/accounts/list` in `Associations_List[].NumofSignatories` (a STRING
 * on the wire, e.g. "1" / "02"). The BFF adapter (`normaliseAssociation`,
 * `coerceSignatories`) already coerces it to a number and threads it onto the
 * shared `Account` shape as the `Signatories` field — the same field the
 * dashboard tile (#92) reads. This helper centralises the ">1" decision so every
 * one of the six blocked action-areas applies the SAME rule and can never drift.
 *
 * The exact agent-facing block message (verbatim, @dylan219) is
 * {@link MULTIPLE_SIGNATORIES_MESSAGE}. Purely a decision + copy helper — the
 * authoritative enforcement is applied per-action in the screen AND server-side.
 */

/**
 * The exact agent-facing message shown when a blocked action is attempted on an
 * account that requires more than one signatory (verbatim, @dylan219, #799).
 */
export const MULTIPLE_SIGNATORIES_MESSAGE =
  'Greater than 1 signature required - Check Mandate';

/**
 * The minimal account shape this helper reads: the normalised `Signatories`
 * count the BFF threads onto every `customer/accounts/list` row from the
 * gateway's `NumofSignatories`. It may arrive as a number (the normalised shape)
 * or, defensively, as the raw string the wire carries.
 */
export interface SignatoryCountAccount {
  Signatories?: number | string;
  /**
   * The raw gateway field name, accepted as a fallback so this helper still
   * works if handed an un-normalised association row directly.
   */
  NumofSignatories?: number | string;
}

/**
 * Coerce a signatory count (number or string, e.g. `2` / "02") to a finite
 * number, or `undefined` when it is absent / non-numeric — so an unknown count
 * is never treated as ">1".
 */
function coerceCount(value: number | string | undefined): number | undefined {
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * True when the account requires MORE THAN ONE signatory to transact (#799).
 *
 * Reads the normalised `Signatories` field first (the BFF projection), falling
 * back to the raw `NumofSignatories` wire field. Returns `false` when the count
 * is absent / unknown / non-numeric — the block only fires on a KNOWN count > 1,
 * so a missing count never blocks a legitimate single-signature account.
 */
export function requiresMultipleSignatories(
  account: SignatoryCountAccount | null | undefined,
): boolean {
  if (!account) return false;
  const count = coerceCount(account.Signatories ?? account.NumofSignatories);
  return count !== undefined && count > 1;
}

/**
 * The sentinel `ResultCode` the BFF returns on a blocked write (#799) — mirrors
 * `MULTIPLE_SIGNATORIES_BLOCK_CODE` on the server. A deep-linked / re-rendered
 * submit still reaches the route and gets refused with this code; the screens
 * map it onto the verbatim block message inline (no code prefix) via
 * {@link signatoryBlockMessage}.
 */
export const MULTIPLE_SIGNATORIES_BLOCK_CODE = 'MULTIPLE_SIGNATORIES';

/**
 * When a write response carries the server's multi-signatory sentinel
 * (`ResultCode: 'MULTIPLE_SIGNATORIES'`), return the verbatim agent-facing
 * block message for the inline error (falling back to the client copy when the
 * server relayed none). Returns `null` for any OTHER outcome so the caller can
 * fall through to its gateway-decline formatter. Used by every blocked
 * action-area's inline-error path (#799; the #80/#82/#94 beside-the-button rule).
 */
export function signatoryBlockMessage(result: {
  ResultCode?: string;
  ResultMessage?: string;
}): string | null {
  if ((result?.ResultCode ?? '').trim() !== MULTIPLE_SIGNATORIES_BLOCK_CODE)
    return null;
  return (result.ResultMessage ?? '').trim() || MULTIPLE_SIGNATORIES_MESSAGE;
}
